import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useBilling } from "@/hooks/useBilling";
import { BookmarkPlus, Loader2, CheckCircle2, AlertTriangle, ArrowLeft } from "lucide-react";

export default function TemplateImportPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { subscription, loading: billingLoading } = useBilling();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);

  const hasAccess = subscription?.status === "active" && ["pro", "crew"].includes(subscription?.plan);

  useEffect(() => {
    if (!token) {
      setError("This share link is missing its token.");
      setLoading(false);
      return;
    }
    api.get(`/templates/share/preview?token=${encodeURIComponent(token)}`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Couldn't load shared template"))
      .finally(() => setLoading(false));
  }, [token]);

  const doImport = async () => {
    setImporting(true);
    try {
      await api.post("/templates/import", { token });
      toast.success("Template added to your library.");
      navigate("/app/templates");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto" data-testid="template-import-page">
      <Link to="/app/templates" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to templates
      </Link>

      {loading || billingLoading ? (
        <Card className="p-10 text-center"><Loader2 className="h-6 w-6 text-orange-500 animate-spin mx-auto" /></Card>
      ) : error ? (
        <Card className="p-10 text-center border-rose-200 bg-rose-50/50" data-testid="import-error">
          <AlertTriangle className="h-8 w-8 text-rose-500 mx-auto mb-3" />
          <h1 className="font-display text-xl font-bold text-slate-900">{error}</h1>
          <Button onClick={() => navigate("/app/templates")} variant="outline" className="mt-4">Back</Button>
        </Card>
      ) : (
        <Card className="p-7 border-slate-200">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-orange-600 mb-1">
            <BookmarkPlus className="h-3.5 w-3.5" /> Shared template
          </div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{preview.name}</h1>
          <p className="text-sm text-slate-500 mt-1">Shared by <strong>{preview.shared_by}</strong></p>
          <div className="flex gap-2 mt-4 flex-wrap">
            <Badge variant="outline" className="border-slate-200 text-slate-700">{preview.trade_category}</Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-700">{preview.quote_type}</Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-700">{preview.tone}</Badge>
            <Badge variant="outline" className="border-slate-200 text-slate-700">{preview.num_items} items · {preview.itemized ? "itemized" : "flat"}</Badge>
          </div>
          {preview.rough_notes_template && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1">Starter notes</div>
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap">{preview.rough_notes_template}</p>
            </div>
          )}

          {!hasAccess ? (
            <div className="mt-6 rounded-md border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900" data-testid="import-upgrade-gate">
              Importing saved templates requires a Pro or Crew plan.
              <Link to="/pricing" className="block mt-2 font-medium underline">View plans →</Link>
            </div>
          ) : (
            <Button
              onClick={doImport} disabled={importing}
              data-testid="confirm-import-button"
              className="mt-6 w-full bg-orange-500 hover:bg-orange-600 text-white"
            >
              {importing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding…</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Add to my templates</>}
            </Button>
          )}
        </Card>
      )}
    </div>
  );
}
